import React, { useLayoutEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { get } from '../../utils/api';
import { formatCurrencyDisplay } from '../portal/portalDonateShared';

/** Derive the product-page slug from a WooCommerce product URL. */
function productSlugFromUrl(productUrl) {
  const raw = String(productUrl || '').trim();
  if (!raw) return '';
  const last = raw.split('?')[0].replace(/\/+$/, '').split('/').filter(Boolean).pop() || '';
  return last.toLowerCase();
}

function productPageTo(item) {
  const slug = productSlugFromUrl(item.product_url);
  if (!slug) return null;
  return `/product/${slug}?product_id=${item.product_id}`;
}

export default function PublicDonateAvailableLingasPage() {
  const navigate = useNavigate();
  const { loading: authLoading, staff } = useAuth();
  const { donateMode, setGuestMode } = usePublicDonate();
  const hasPortalSession = !authLoading && !!staff?.portal;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [groups, setGroups] = React.useState([]);
  const [total, setTotal] = React.useState(0);

  useLayoutEffect(() => {
    if (authLoading) return;
    if (hasPortalSession) return;
    if (!donateMode) setGuestMode();
  }, [authLoading, hasPortalSession, donateMode, setGuestMode]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await get('/public/facets/kotilinga-available-lingas', {});
        if (cancelled) return;
        setGroups(Array.isArray(res?.groups) ? res.groups : []);
        setTotal(Number(res?.total) || 0);
      } catch {
        if (!cancelled) setError('Could not load available lingas. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack spacing={2}>
      <Box>
        <Button
          variant="text"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/donate/kotilinga'))}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, color: 'primary.dark', px: 1 }}
        >
          Back to Kotilinga Kshetram
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid #e1bee7',
          bgcolor: '#faf5ff',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#4a148c' }}>
          Available Special Lingas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Special lingas in the Kotilinga Kshetram that are still available to sponsor. Select one to
          open its page and donate.
        </Typography>

        {loading ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading available lingas…
            </Typography>
          </Stack>
        ) : null}

        {!loading && error ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && !error && total === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            All special lingas are currently sponsored. Please check back later.
          </Alert>
        ) : null}

        {!loading && !error && total > 0 ? (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              {total} available
            </Typography>
            <Stack spacing={2.5}>
              {groups.map((group) => (
                <Box key={group.category}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#4a148c', mb: 1 }}>
                    {group.category}
                  </Typography>
                  <Stack spacing={1}>
                    {group.items.map((item) => {
                      const to = productPageTo(item);
                      return (
                        <Paper
                          key={item.product_id}
                          elevation={1}
                          sx={{
                            p: { xs: 1.25, sm: 1.5 },
                            borderRadius: 1.5,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {item.name}
                            </Typography>
                            {item.price_num != null ? (
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
                                {formatCurrencyDisplay(item.price_num)}
                              </Typography>
                            ) : null}
                          </Box>
                          {to ? (
                            <Button
                              component={NavLink}
                              to={to}
                              variant="contained"
                              color="primary"
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                flexShrink: 0,
                                alignSelf: { xs: 'flex-start', sm: 'center' },
                              }}
                            >
                              Donate
                            </Button>
                          ) : null}
                        </Paper>
                      );
                    })}
                  </Stack>
                  <Divider sx={{ mt: 2, borderColor: '#e1bee7' }} />
                </Box>
              ))}
            </Stack>
          </>
        ) : null}
      </Paper>
    </Stack>
  );
}
